import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Fab, SpeedDial, SpeedDialAction, Typography } from '@mui/material';
import React from 'react';

import { useDownloadScoreView, useGetBsimmAssessmentView } from '@/api';
import { useAuth } from '@/auth';
import { Loader } from '@/shared/components';
import { ERoles } from '@/shared/duck/global-enums.ts';
import { muiPalette } from '@/shared/general-theme';

interface AssessmentViewProps {
  assessmentId: string;
  setOpenCreateEditModal: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenDeleteModal: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenAdminToolsModal: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenScoreUpdateModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const AssessmentView: React.FC<AssessmentViewProps> = React.memo(
  ({ assessmentId, setOpenDeleteModal, setOpenCreateEditModal, setOpenAdminToolsModal, setOpenScoreUpdateModal }) => {
    const iframeRef = React.useRef<HTMLIFrameElement>(null);
    const [isFullscreen, setIsFullscreen] = React.useState(false);
    const { user } = useAuth();
    const isAdmin = React.useMemo(
      () =>
        user?.roles?.includes(ERoles.Admin) ?? false,
      [user],
    );

    const { bsimmAssessmentView, isBsimmLoading } = useGetBsimmAssessmentView({
      assessmentId,
    });

    const {
      downloadScoreViewRefetch,
    } = useDownloadScoreView(assessmentId);

    React.useEffect(() => {
      const handleFullscreenChange = () => {
        setIsFullscreen(!!document.fullscreenElement);
      };

      document.addEventListener('fullscreenchange', handleFullscreenChange);
      return () => {
        document.removeEventListener('fullscreenchange', handleFullscreenChange);
      };
    }, []);

    const toggleFullscreen = () => {
      if (!iframeRef.current) {
        return;
      }

      if (!isFullscreen) {
        iframeRef.current.requestFullscreen().then(() => {
          setIsFullscreen(true);
        }).catch((err) => {
          console.error('Error when trying to enable fullscreen mode:', err);
        });
      } else {
        document.exitFullscreen().then(() => {
          setIsFullscreen(false);
        }).catch((err) => {
          console.error('Error exiting fullscreen mode:', err);
        });
      }
    };

    const handleEdit = React.useCallback(() => {
      setOpenCreateEditModal(true);
    }, [setOpenCreateEditModal]);

    const handleDelete = React.useCallback(() => {
      setOpenDeleteModal(true);
    }, [setOpenDeleteModal]);

    const handleOpenAdminToolsModal = React.useCallback(() => {
      setOpenAdminToolsModal(true);
    }, [setOpenAdminToolsModal]);

    const handleOpenScoreUpdateModal = React.useCallback(() => {
      setOpenScoreUpdateModal(true);
    }, [setOpenScoreUpdateModal]);

    const handleDownloadScoreSheet = React.useCallback(async () => {
      try {
        const result = await downloadScoreViewRefetch();

        if (!result.data?.data) {
          return;
        }

        const blob = new Blob([result.data.data], {
          type: result.data.headers['content-type'],
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        const filename = result.data.headers['content-disposition']
                           ?.split('filename=')[1]
                           ?.replace(/["']/g, '') || 'scoresheet.xlsx';

        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();

        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      } catch (error) {
        console.error('Error downloading score sheet:', error);
      }
    }, [downloadScoreViewRefetch]);

    const actions = React.useMemo(() => {
      const baseActions = [
        { icon: <EditIcon />, name: 'Edit Assessment', onClick: handleEdit },
        { icon: <EditNoteOutlinedIcon />, name: 'Update Scores', onClick: handleOpenScoreUpdateModal },
        { icon: <FileDownloadOutlinedIcon />, name: 'Download Score Sheet', onClick: handleDownloadScoreSheet },
        { icon: <DeleteOutlinedIcon />, name: 'Delete Assessment', onClick: handleDelete },
      ];

      if (isAdmin) {
        return [
          ...baseActions,
          {
            icon: <AdminPanelSettingsOutlinedIcon />,
            name: 'Admin Tools',
            onClick: handleOpenAdminToolsModal,
          },
        ];
      }

      return baseActions;
    }, [isAdmin, handleEdit, handleDownloadScoreSheet, handleDelete, handleOpenAdminToolsModal]);

    const iframeContent = React.useMemo(() => {
      const modifiedHtml = `
        <style>
          html, body {
            margin: 0;
            padding: 0;
            width: 100%;
            height: 100%;
            background-color: white;
          }
          .plotly-graph-div {
            width: 100% !important;
            max-width: 100%;
            box-sizing: border-box;
          }
        </style>
        ${bsimmAssessmentView}
  `;

      return (
        <iframe
          ref={iframeRef}
          srcDoc={modifiedHtml}
          height='100%'
          width='100%'
          style={{
            border: 'none',
            display: 'block',
          }}
          allowFullScreen
          title='BSIMM Assessment View'
          sandbox='allow-scripts'
        />
      );
    }, [bsimmAssessmentView]);

    return (
      <Box
        display='flex'
        width='100%'
        height='100%'
        sx={{
          border: `1px solid ${muiPalette.grey[100]}`,
          borderRadius: '0.5rem',
          overflow: 'hidden',
          position: 'relative',
          padding: '0.5rem',
          backgroundColor: 'white',
        }}
      >
        {isBsimmLoading ? (
          <Loader fullscreen />
        ) : bsimmAssessmentView ? (
          <>
            {iframeContent}
          </>
        ) : (
          <Box
            display='flex'
            width='100%'
            height='100%'
            justifyContent='center'
            alignItems='center'
          >
            <Typography variant='body1' color='text.secondary'>
              Unable to visualize content. Data is missing or unavailable.
            </Typography>
          </Box>
        )}

        <SpeedDial
          ariaLabel='Additional actions'
          sx={{
            position: 'absolute',
            bottom: 80,
            right: 16,
          }}
          FabProps={{
            size: 'medium',
          }}
          icon={<MoreVertIcon />}
        >
          {actions.map((action) => (
            <SpeedDialAction
              key={action.name}
              icon={action.icon}
              tooltipTitle={action.name}
              onClick={action.onClick}
            />
          ))}
        </SpeedDial>

        <Fab
          color='primary'
          size='medium'
          onClick={toggleFullscreen}
          sx={{
            position: 'absolute',
            bottom: 16,
            right: 20,
          }}
        >
          {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
        </Fab>
      </Box>
    );
  },
);

export default AssessmentView;